/**
 * The one thing the app has to tell the service worker
 *
 * Audio caches are namespaced per account (FR-027), so the worker cannot derive
 * the name — it has no session. Searching every `dissona-audio::*` cache would
 * work but would also let a second account on the same browser read the first
 * one's bytes, which is exactly the isolation this namespacing exists to
 * provide.
 *
 * So the app publishes the active cache name into a fixed-name database that
 * both sides can open without knowing anything about the session. Fixed name,
 * no circularity, and it survives the worker being restarted.
 */

import { openDbSafely } from './idb';

const DB_NAME = 'dissona-sw';
const STORE = 'meta';
const KEY = 'audioCache';

// Held rather than reopened per call. Every `openDB` is a separate connection,
// and one per publish/read left a pile of them open — each one another thing
// that has to close before the next schema bump can start.
let dbPromise = null;

const open = () => {
  if (!dbPromise) {
    dbPromise = openDbSafely(DB_NAME, 1, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
      },
      onLost: () => {
        dbPromise = null;
      },
    });
    // Cached before anyone awaits it; see the same guard in `repository.js`.
    dbPromise.catch(() => {});
  }
  return dbPromise;
};

export const publishAudioCacheName = async (name) => {
  try {
    await (await open()).put(STORE, name, KEY);
  } catch (e) {
    console.warn('swHandshake: could not publish audio cache name', e);
  }
};

export const readAudioCacheName = async () => {
  try {
    return (await (await open()).get(STORE, KEY)) || null;
  } catch (e) {
    return null;
  }
};

export const clearAudioCacheName = async () => {
  try {
    await (await open()).delete(STORE, KEY);
  } catch (e) {
    console.warn('swHandshake: could not clear audio cache name', e);
  }
};
